import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import { X, User, FileText, Palette, Save, AlertCircle, MessageSquare, RotateCcw, Brain, CreditCard } from 'lucide-react';
import { useSubscription } from '../contexts/SubscriptionContext';
import './SettingsModal.css';

function SettingsModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  // Subscription state
  const { subscription, usage, plans, loading: subLoading, startCheckout, cancelSubscription, refresh } = useSubscription();
  const [cancelLoading, setCancelLoading] = useState(false);

  // Profile state
  const [profile, setProfile] = useState({
    id: null,
    name: '',
    height_cm: 160.0,
    weight_kg: '',
    skate_height_cm: 5.0,
    gender: '',
    skill_level: 'senior',
    birth_date: ''
  });

  // Memory editing state
  const [coreContext, setCoreContext] = useState('');
  const [memorizedNotes, setMemorizedNotes] = useState([]);

  // Theme state
  const [theme, setTheme] = useState('gold');

  // Conversation history settings state
  const [conversationSettings, setConversationSettings] = useState({
    conversation_history_count: { value: 2, min: 0, max: 10, description: '' },
    conversation_user_chars: { value: 150, min: 50, max: 500, description: '' },
    conversation_assistant_chars: { value: 250, min: 50, max: 1000, description: '' },
    video_analysis_save_to_chat: { value: false, type: 'boolean', description: '' }
  });

  // Load profile on mount
  useEffect(() => {
    if (isOpen) {
      loadProfile();
      loadMemory();
      loadTheme();
      loadConversationSettings();
    }
  }, [isOpen]);

  const loadProfile = async () => {
    try {
      const response = await api.get(`/settings/profile`);
      if (response.data.success && response.data.profile) {
        setProfile(response.data.profile);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const loadMemory = async () => {
    try {
      const [coreResponse, notesResponse] = await Promise.all([
        api.get(`/settings/files/core-context`),
        api.get(`/settings/files/memorized-notes`)
      ]);

      if (coreResponse.data.success) {
        setCoreContext(coreResponse.data.content);
      }
      if (notesResponse.data.success) {
        setMemorizedNotes(notesResponse.data.notes || []);
      }
    } catch (error) {
      console.error('Error loading memory:', error);
    }
  };

  const loadTheme = async () => {
    try {
      // Try to load from backend first (for cross-device sync)
      const response = await api.get('/settings/theme');
      if (response.data.success && response.data.theme) {
        setTheme(response.data.theme);
        localStorage.setItem('theme', response.data.theme);
        document.body.setAttribute('data-theme', response.data.theme);
        return;
      }
    } catch (error) {
      console.log('Using localStorage theme (backend unavailable)');
    }

    // Fall back to localStorage
    const savedTheme = localStorage.getItem('theme') || 'gold';
    setTheme(savedTheme);
    document.body.setAttribute('data-theme', savedTheme);
  };

  const handleProfileSave = async () => {
    setLoading(true);
    setSaveStatus(null);

    try {
      const response = await api.put(`/settings/profile`, profile);

      if (response.data.success) {
        setSaveStatus({ type: 'success', message: 'Profile saved successfully!' });
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleCoreContextSave = async () => {
    setLoading(true);
    setSaveStatus(null);

    try {
      const response = await api.put(`/settings/files/core-context`, { content: coreContext });

      if (response.data.success) {
        setSaveStatus({ type: 'success', message: 'Working Memory saved successfully!' });
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error saving working memory:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save working memory' });
    } finally {
      setLoading(false);
    }
  };

  const handleMemorizedNoteSave = async (noteId, content) => {
    setLoading(true);
    setSaveStatus(null);

    try {
      const response = await api.put(`/settings/files/memorized-notes/${noteId}`, { content });

      if (response.data.success) {
        setSaveStatus({ type: 'success', message: 'Memorized note saved successfully!' });
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error saving memorized note:', error);
      setSaveStatus({ type: 'error', message: 'Failed to save memorized note' });
    } finally {
      setLoading(false);
    }
  };

  const updateMemorizedNote = (noteId, newContent) => {
    setMemorizedNotes(prev =>
      prev.map(note =>
        note.id === noteId ? { ...note, content: newContent } : note
      )
    );
  };

  const handleThemeChange = async (newTheme) => {
    // Apply immediately for responsive UX
    setTheme(newTheme);
    document.body.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);

    // Save to backend for cross-device sync
    try {
      await api.put('/settings/theme', { theme: newTheme });
      setSaveStatus({ type: 'success', message: 'Theme updated!' });
    } catch (error) {
      console.error('Error saving theme to backend:', error);
      // Theme still works locally even if backend fails
      setSaveStatus({ type: 'success', message: 'Theme updated locally!' });
    }
    setTimeout(() => setSaveStatus(null), 2000);
  };

  const loadConversationSettings = async () => {
    try {
      const response = await api.get(`/settings/conversation`);
      if (response.data.success) {
        setConversationSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error loading conversation settings:', error);
    }
  };

  const handleConversationSettingsSave = async () => {
    setLoading(true);
    setSaveStatus(null);

    try {
      const payload = {
        conversation_history_count: conversationSettings.conversation_history_count.value,
        conversation_user_chars: conversationSettings.conversation_user_chars.value,
        conversation_assistant_chars: conversationSettings.conversation_assistant_chars.value,
        video_analysis_save_to_chat: conversationSettings.video_analysis_save_to_chat.value
      };

      const response = await api.put(`/settings/conversation`, payload);

      if (response.data.success) {
        setSaveStatus({ type: 'success', message: 'Conversation settings saved successfully!' });
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error saving conversation settings:', error);
      setSaveStatus({ type: 'error', message: error.response?.data?.detail || 'Failed to save settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleConversationSettingsReset = async () => {
    setLoading(true);
    setSaveStatus(null);

    try {
      const response = await api.post(`/settings/conversation/reset`);

      if (response.data.success) {
        setConversationSettings(response.data.settings);
        setSaveStatus({ type: 'success', message: 'Settings reset to defaults!' });
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error resetting conversation settings:', error);
      setSaveStatus({ type: 'error', message: 'Failed to reset settings' });
    } finally {
      setLoading(false);
    }
  };

  const updateConversationSetting = (key, value) => {
    setConversationSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: prev[key].type === 'boolean' ? value : parseInt(value)
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
            <span className="close-label">Close</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <User size={18} />
            Profile
          </button>
          <button
            className={`settings-tab ${activeTab === 'conversation' ? 'active' : ''}`}
            onClick={() => setActiveTab('conversation')}
          >
            <MessageSquare size={18} />
            Conversation
          </button>
          <button
            className={`settings-tab ${activeTab === 'memory' ? 'active' : ''}`}
            onClick={() => setActiveTab('memory')}
          >
            <Brain size={18} />
            Memory
          </button>
          <button
            className={`settings-tab ${activeTab === 'theme' ? 'active' : ''}`}
            onClick={() => setActiveTab('theme')}
          >
            <Palette size={18} />
            Theme
          </button>
          <button
            className={`settings-tab ${activeTab === 'subscription' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscription')}
          >
            <CreditCard size={18} />
            Subscription
          </button>
        </div>

        {/* Content */}
        <div className="settings-content">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="settings-section">
              <h3>Skater Profile</h3>
              <p className="section-description">
                Configure your physical measurements for accurate video analysis calibration.
              </p>

              <div className="form-grid">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    placeholder="Enter your name"
                  />
                </div>

                <div className="form-group">
                  <label>Height (cm) *</label>
                  <input
                    type="number"
                    step="0.1"
                    value={profile.height_cm}
                    onChange={(e) => setProfile({ ...profile, height_cm: parseFloat(e.target.value) })}
                    placeholder="160.0"
                  />
                  <small>Height without skates</small>
                </div>

                <div className="form-group">
                  <label>Weight (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={profile.weight_kg || ''}
                    onChange={(e) => setProfile({ ...profile, weight_kg: e.target.value ? parseFloat(e.target.value) : null })}
                    placeholder="Optional"
                  />
                </div>

                <div className="form-group">
                  <label>Skate Height (cm)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={profile.skate_height_cm}
                    onChange={(e) => setProfile({ ...profile, skate_height_cm: parseFloat(e.target.value) })}
                    placeholder="5.0"
                  />
                  <small>Blade + boot height (default: 5cm)</small>
                </div>

                <div className="form-group">
                  <label>Competition Level</label>
                  <select
                    value={profile.skill_level}
                    onChange={(e) => setProfile({ ...profile, skill_level: e.target.value })}
                  >
                    <option value="pre_preliminary">Pre-Preliminary</option>
                    <option value="preliminary">Preliminary</option>
                    <option value="pre_juvenile">Pre-Juvenile</option>
                    <option value="juvenile">Juvenile</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="novice">Novice</option>
                    <option value="junior">Junior</option>
                    <option value="senior">Senior (Olympic)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Gender</label>
                  <select
                    value={profile.gender || ''}
                    onChange={(e) => setProfile({ ...profile, gender: e.target.value })}
                  >
                    <option value="">Select...</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button className="save-button" onClick={handleProfileSave} disabled={loading || !profile.name || !profile.height_cm}>
                  <Save size={18} />
                  {loading ? 'Saving...' : 'Save Profile'}
                </button>
              </div>

              <div className="info-box">
                <AlertCircle size={16} />
                <span>Height settings will be applied to new video analyses only.</span>
              </div>
            </div>
          )}

          {/* Conversation Tab */}
          {activeTab === 'conversation' && (
            <div className="settings-section">
              <h3>Conversation History Settings</h3>
              <p className="section-description">
                Configure how much conversation history is included in each prompt to the AI.
                More history provides better context but uses more tokens.
              </p>

              <div className="form-grid">
                <div className="form-group full-width">
                  <label>
                    Number of Recent Exchanges
                    <span className="setting-value">{conversationSettings.conversation_history_count.value}</span>
                  </label>
                  <input
                    type="range"
                    min={conversationSettings.conversation_history_count.min}
                    max={conversationSettings.conversation_history_count.max}
                    value={conversationSettings.conversation_history_count.value}
                    onChange={(e) => updateConversationSetting('conversation_history_count', e.target.value)}
                    className="slider"
                  />
                  <small>
                    How many recent conversation exchanges to include (0-10).
                    Default: 2. Set to 0 to disable conversation history.
                  </small>
                </div>

                <div className="form-group full-width">
                  <label>
                    User Message Characters
                    <span className="setting-value">{conversationSettings.conversation_user_chars.value}</span>
                  </label>
                  <input
                    type="range"
                    min={conversationSettings.conversation_user_chars.min}
                    max={conversationSettings.conversation_user_chars.max}
                    step="10"
                    value={conversationSettings.conversation_user_chars.value}
                    onChange={(e) => updateConversationSetting('conversation_user_chars', e.target.value)}
                    className="slider"
                  />
                  <small>
                    Maximum characters from your messages (50-500).
                    Default: 150. Longer captures more detail.
                  </small>
                </div>

                <div className="form-group full-width">
                  <label>
                    Assistant Response Characters
                    <span className="setting-value">{conversationSettings.conversation_assistant_chars.value}</span>
                  </label>
                  <input
                    type="range"
                    min={conversationSettings.conversation_assistant_chars.min}
                    max={conversationSettings.conversation_assistant_chars.max}
                    step="10"
                    value={conversationSettings.conversation_assistant_chars.value}
                    onChange={(e) => updateConversationSetting('conversation_assistant_chars', e.target.value)}
                    className="slider"
                  />
                  <small>
                    Maximum characters from Lumi's responses (50-1000).
                    Default: 250. Longer preserves technical explanations.
                  </small>
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={conversationSettings.video_analysis_save_to_chat.value}
                      onChange={(e) => updateConversationSetting('video_analysis_save_to_chat', e.target.checked)}
                    />
                    <span>Save video analysis coaching feedback to chat and notable events</span>
                  </label>
                  <small>
                    When enabled, video analysis coaching feedback will appear in the coaching chat and be saved to notable events.
                    Disable this when analyzing videos for other skaters to keep your personal coaching history clean.
                  </small>
                </div>
              </div>

              <div className="info-box">
                <AlertCircle size={16} />
                <div>
                  <strong>Token Usage Estimate:</strong>
                  <p>
                    Current settings use approximately{' '}
                    <strong>
                      {Math.round((conversationSettings.conversation_history_count.value *
                        (conversationSettings.conversation_user_chars.value +
                         conversationSettings.conversation_assistant_chars.value)) / 4)}
                    </strong> tokens per request.
                  </p>
                </div>
              </div>

              <div className="form-actions">
                <button className="save-button" onClick={handleConversationSettingsSave} disabled={loading}>
                  <Save size={18} />
                  {loading ? 'Saving...' : 'Save Settings'}
                </button>
                <button className="reset-button" onClick={handleConversationSettingsReset} disabled={loading}>
                  <RotateCcw size={18} />
                  Reset to Defaults
                </button>
              </div>
            </div>
          )}

          {/* Memory Tab */}
          {activeTab === 'memory' && (
            <div className="settings-section">
              <h3>Edit Memory</h3>
              <p className="section-description">
                Edit your Working Memory (Lumi's context about you) and Memorized Coach Notes directly.
              </p>

              <div className="file-editor">
                <div className="file-section">
                  <div className="file-header">
                    <h4>Working Memory (Core Context)</h4>
                    <button className="save-button-small" onClick={handleCoreContextSave} disabled={loading}>
                      <Save size={16} />
                      Save
                    </button>
                  </div>
                  <textarea
                    className="file-textarea"
                    value={coreContext}
                    onChange={(e) => setCoreContext(e.target.value)}
                    placeholder="Loading..."
                    rows={12}
                  />
                </div>

                <div className="file-section">
                  <div className="file-header">
                    <h4>Memorized Coach Notes ({memorizedNotes.length})</h4>
                  </div>
                  {memorizedNotes.length === 0 ? (
                    <div className="empty-notes">
                      <p>No memorized notes yet. Memorize notes from the Notes tab to see them here.</p>
                    </div>
                  ) : (
                    <div className="memorized-notes-list">
                      {memorizedNotes.map((note) => (
                        <div key={note.id} className="memorized-note-item">
                          <div className="note-header">
                            <span className="note-title">{note.title || 'Untitled Note'}</span>
                            <span className="note-category">{note.category || 'General'}</span>
                            <button
                              className="save-button-small"
                              onClick={() => handleMemorizedNoteSave(note.id, note.content)}
                              disabled={loading}
                            >
                              <Save size={14} />
                              Save
                            </button>
                          </div>
                          <textarea
                            className="file-textarea note-textarea"
                            value={note.content}
                            onChange={(e) => updateMemorizedNote(note.id, e.target.value)}
                            placeholder="Note content..."
                            rows={6}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Theme Tab */}
          {activeTab === 'theme' && (
            <div className="settings-section">
              <h3>Appearance</h3>
              <p className="section-description">
                Choose your preferred color theme.
              </p>

              <div className="theme-options">
                <div
                  className={`theme-card ${theme === 'light' ? 'active' : ''}`}
                  onClick={() => handleThemeChange('light')}
                >
                  <div className="theme-preview light-preview">
                    <div className="preview-header"></div>
                    <div className="preview-content">
                      <div className="preview-line"></div>
                      <div className="preview-line short"></div>
                    </div>
                  </div>
                  <h4>Light Mode</h4>
                  <p>Clean and bright interface</p>
                </div>

                <div
                  className={`theme-card ${theme === 'dark' ? 'active' : ''}`}
                  onClick={() => handleThemeChange('dark')}
                >
                  <div className="theme-preview dark-preview">
                    <div className="preview-header"></div>
                    <div className="preview-content">
                      <div className="preview-line"></div>
                      <div className="preview-line short"></div>
                    </div>
                  </div>
                  <h4>Dark Mode</h4>
                  <p>Easy on the eyes</p>
                </div>

                <div
                  className={`theme-card ${theme === 'glass' ? 'active' : ''}`}
                  onClick={() => handleThemeChange('glass')}
                >
                  <div className="theme-preview glass-preview">
                    <div className="preview-header glass-header"></div>
                    <div className="preview-content glass-content">
                      <div className="preview-line glass-line"></div>
                      <div className="preview-line glass-line short"></div>
                    </div>
                  </div>
                  <h4>Liquid Glass</h4>
                  <p>iOS-inspired frosted glass</p>
                </div>

                <div
                  className={`theme-card ${theme === 'gold' ? 'active' : ''}`}
                  onClick={() => handleThemeChange('gold')}
                >
                  <div className="theme-preview gold-preview">
                    <div className="preview-header gold-header"></div>
                    <div className="preview-content gold-content">
                      <div className="preview-line gold-line"></div>
                      <div className="preview-line gold-line short"></div>
                    </div>
                  </div>
                  <h4>Premium Gold</h4>
                  <p>Luxurious golden elegance</p>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="settings-section subscription-section">
              <h3>Subscription</h3>

              {/* Dev Mode Banner */}
              {subscription?.dev_mode && (
                <div className="dev-mode-banner">
                  <span className="dev-mode-icon">🛠️</span>
                  <div className="dev-mode-text">
                    <strong>Development Mode</strong>
                    <p>All subscription features are bypassed. Configure Stripe keys to enable payments.</p>
                  </div>
                </div>
              )}

              {subLoading ? (
                <div className="subscription-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading subscription...</p>
                </div>
              ) : (
                <>
                  {/* Current Subscription */}
                  {subscription && subscription.status !== 'none' && (
                    <div className="current-subscription">
                      <h4>Current Plan</h4>
                      <div className="subscription-card active">
                        <div className="plan-header">
                          <span className="plan-name">{subscription.plan_name}</span>
                          <span className={`status-badge ${subscription.status}`}>
                            {subscription.status}
                          </span>
                        </div>
                        <div className="subscription-details">
                          <div className="detail-row">
                            <span className="label">Price</span>
                            <span className="value">${(subscription.price / 100).toFixed(2)}/month</span>
                          </div>
                          {usage && (
                            <div className="detail-row">
                              <span className="label">Video Uploads</span>
                              <span className="value">
                                {usage.limit === -1 ? (
                                  <span className="unlimited">Unlimited</span>
                                ) : (
                                  <>
                                    {usage.used} / {usage.limit}
                                    <span className="remaining"> ({usage.remaining} remaining)</span>
                                  </>
                                )}
                              </span>
                            </div>
                          )}
                          {subscription.current_period_end && (
                            <div className="detail-row">
                              <span className="label">
                                {subscription.cancel_at_period_end ? 'Access Until' : 'Renews On'}
                              </span>
                              <span className="value">
                                {new Date(subscription.current_period_end).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>

                        {subscription.cancel_at_period_end && (
                          <div className="cancellation-notice">
                            Your subscription will end on {new Date(subscription.current_period_end).toLocaleDateString()}.
                            You'll retain access until then.
                          </div>
                        )}

                        {subscription.status === 'active' && !subscription.cancel_at_period_end && !subscription.dev_mode && (
                          <button
                            className="cancel-button"
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to cancel your subscription?')) {
                                setCancelLoading(true);
                                try {
                                  await cancelSubscription();
                                } catch (err) {
                                  console.error('Cancel error:', err);
                                }
                                setCancelLoading(false);
                              }
                            }}
                            disabled={cancelLoading}
                          >
                            {cancelLoading ? 'Canceling...' : 'Cancel Subscription'}
                          </button>
                        )}

                        {subscription.dev_mode && (
                          <div className="dev-notice">
                            <small>Cancel functionality disabled in development mode</small>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Available Plans */}
                  <div className="available-plans">
                    <h4>{subscription?.status === 'none' ? 'Choose a Plan' : 'Upgrade Plan'}</h4>
                    <div className="plans-grid">
                      {plans.map((plan) => {
                        const isCurrent = subscription?.plan_id === plan.id;
                        const isRecommended = plan.name === 'Elite';
                        const isPlaceholder = !plan.stripe_price_id || plan.stripe_price_id?.includes('placeholder');
                        const isDisabled = isCurrent || subLoading || subscription?.dev_mode || isPlaceholder;

                        return (
                          <div
                            key={plan.id}
                            className={`plan-card ${isCurrent ? 'current' : ''} ${isRecommended ? 'recommended' : ''}`}
                          >
                            {isRecommended && <span className="recommended-badge">Best Value</span>}
                            <h4>{plan.name}</h4>
                            <div className="plan-price">
                              ${((plan.price_cents || plan.price) / 100).toFixed(0)}
                              <span className="interval">/month</span>
                            </div>
                            <p className="plan-description">{plan.description}</p>
                            <ul className="plan-features">
                              {plan.name === 'Pro' ? (
                                <>
                                  <li>50 video uploads/month</li>
                                  <li>AI coaching feedback</li>
                                  <li>Training planning</li>
                                  <li>Video comparison</li>
                                  <li>Coach notes</li>
                                </>
                              ) : (
                                <>
                                  <li>Unlimited video uploads</li>
                                  <li>AI coaching feedback</li>
                                  <li>Training planning</li>
                                  <li>Video comparison</li>
                                  <li>Coach notes</li>
                                  <li>Priority support</li>
                                </>
                              )}
                            </ul>
                            <button
                              className={`plan-button ${isCurrent ? 'current' : ''}`}
                              onClick={() => !isDisabled && startCheckout(plan.id)}
                              disabled={isDisabled}
                            >
                              {isCurrent ? 'Current Plan' : isPlaceholder ? 'Coming Soon' : 'Subscribe'}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Refund Policy */}
                  <div className="refund-policy">
                    <h4>Refund Policy</h4>
                    <p>
                      Full refund within 7 days if no videos have been uploaded.
                      Contact support for assistance.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Status Message */}
        {saveStatus && (
          <div className={`status-message ${saveStatus.type}`}>
            {saveStatus.message}
          </div>
        )}
      </div>
    </div>
  );
}

export default SettingsModal;
